export const AUTO_LINK_URLS = true;

const SAFE_URL = /^(https?:\/\/|mailto:)/i;
const EMAIL_ADDRESS = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const LINK_PATTERN =
  /\b(?:https?:\/\/[^\s<>"']+|mailto:[^\s<>"']+|www\.(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>"']*)?|[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/gi;

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const isAcceptableHref = (href: string): boolean => {
  if (/^mailto:/i.test(href)) {
    return /^mailto:[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(href);
  }
  try {
    const parsed = new URL(href);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }
    const host = parsed.hostname.replace(/\.$/, '');
    if (host === 'localhost') return true;
    const parts = host.split('.');
    if (parts.length < 2) return false;
    const tld = parts[parts.length - 1];
    return (
      /^[a-z]{2,}$/i.test(tld) || parts.every((part) => /^\d{1,3}$/.test(part))
    );
  } catch {
    return false;
  }
};

export const normalizeLinkUrl = (raw: string): string | null => {
  const url = raw.trim();
  if (!url) return null;
  if (SAFE_URL.test(url)) return url;
  if (EMAIL_ADDRESS.test(url)) return `mailto:${url}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null;
  return `https://${url}`;
};

const trimTrailingPunctuation = (raw: string): string => {
  let url = raw;
  let trimmed = true;
  while (url && trimmed) {
    trimmed = false;
    const last = url[url.length - 1];
    if ('.,;:!?\'"'.includes(last) || last === ']') {
      url = url.slice(0, -1);
      trimmed = true;
      continue;
    }
    if (last === ')') {
      const open = (url.match(/\(/g) || []).length;
      const close = (url.match(/\)/g) || []).length;
      if (close > open) {
        url = url.slice(0, -1);
        trimmed = true;
      }
    }
  }
  return url;
};

type TextLink = { start: number; end: number; url: string; href: string };

const findTextLinks = (text: string): TextLink[] => {
  if (!AUTO_LINK_URLS) return [];
  const links: TextLink[] = [];
  const pattern = new RegExp(LINK_PATTERN.source, 'gi');
  let match = pattern.exec(text);
  while (match) {
    const raw = match[0];
    const start = match.index;
    const url = trimTrailingPunctuation(raw);
    const href = url ? normalizeLinkUrl(url) : null;
    if (url && href && isAcceptableHref(href)) {
      links.push({ start, end: start + url.length, url, href });
    }
    if (pattern.lastIndex === start) pattern.lastIndex += 1;
    match = pattern.exec(text);
  }
  return links;
};

export const linkifyPlainText = (text: string): string => {
  const links = findTextLinks(text);
  if (!links.length) return escapeHtml(text);
  let html = '';
  let cursor = 0;
  links.forEach((link) => {
    html += escapeHtml(text.slice(cursor, link.start));
    html += `<a href="${escapeHtml(link.href)}">${escapeHtml(link.url)}</a>`;
    cursor = link.end;
  });
  html += escapeHtml(text.slice(cursor));
  return html;
};

const bareHref = (href: string): string =>
  href.replace(/^mailto:/i, '').replace(/^https?:\/\//i, '');

const refreshAutolinkHrefs = (root: HTMLElement): void => {
  root.querySelectorAll('a').forEach((anchor) => {
    const label = anchor.textContent || '';
    const links = findTextLinks(label);
    const only = links[0];
    if (
      links.length !== 1 ||
      !only ||
      only.start !== 0 ||
      only.end !== label.length
    ) {
      return;
    }
    const current = anchor.getAttribute('href') || '';
    if (current === only.href) return;
    const currentBare = bareHref(current);
    const nextBare = bareHref(only.href);
    if (nextBare.startsWith(currentBare) || currentBare.startsWith(nextBare)) {
      anchor.setAttribute('href', only.href);
    }
  });
};

const linkifyEditorLinks = (root: HTMLElement, forceAll: boolean): void => {
  const selection = window.getSelection();
  const caretNode = selection?.anchorNode ?? null;
  const caretOffset = selection?.anchorOffset ?? 0;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    if (textNode.parentElement?.closest('a')) return;
    const text = textNode.textContent || '';
    const links = findTextLinks(text).filter((link) => {
      if (forceAll) return true;
      if (/\s/.test(text[link.end] || '')) return true;
      const typing =
        caretNode === textNode &&
        caretOffset >= link.start &&
        caretOffset <= link.end;
      return !typing;
    });
    if (!links.length) return;

    const caretHere = caretNode === textNode;
    const fragment = document.createDocumentFragment();
    const pieces: { node: ChildNode; start: number; end: number }[] = [];
    let cursor = 0;
    const pushText = (value: string, start: number) => {
      if (!value) return;
      const node = document.createTextNode(value);
      fragment.appendChild(node);
      pieces.push({ node, start, end: start + value.length });
    };
    links.forEach((link) => {
      pushText(text.slice(cursor, link.start), cursor);
      const anchor = document.createElement('a');
      anchor.setAttribute('href', link.href);
      anchor.textContent = link.url;
      fragment.appendChild(anchor);
      pieces.push({ node: anchor, start: link.start, end: link.end });
      cursor = link.end;
    });
    pushText(text.slice(cursor), cursor);
    textNode.parentNode?.replaceChild(fragment, textNode);

    if (!caretHere || !selection) return;
    const piece =
      pieces.find((item) => caretOffset <= item.end) ||
      pieces[pieces.length - 1];
    if (!piece) return;
    const offset = Math.max(0, caretOffset - piece.start);
    const range = document.createRange();
    if (piece.node.nodeType === Node.TEXT_NODE) {
      range.setStart(
        piece.node,
        Math.min(offset, piece.node.textContent?.length || 0),
      );
    } else {
      const textChild = piece.node.firstChild;
      if (!textChild) {
        range.setStartAfter(piece.node);
      } else {
        range.setStart(
          textChild,
          Math.min(offset, textChild.textContent?.length || 0),
        );
      }
    }
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  });
};

export const applyAutoLinks = (root: HTMLElement, forceAll = false): void => {
  if (!AUTO_LINK_URLS) return;
  linkifyEditorLinks(root, forceAll);
  refreshAutolinkHrefs(root);
};
